import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchemaSupportedFolderItemType} from 'constants/RegistryConstants';
import {SchemaFieldDataType} from 'folder-items/FileConstants';
import {TagSchemaSequenceSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ProcedureSchemaFieldTypeTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import notifications from 'notifications';
import {
  ANY_ENTITY,
  FieldType,
  isRequiredLinkCustomSchemaInterface,
  RequiredLink,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {CustomSchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import {folderItemTypeAndSequenceTypeToItemType} from 'schemas/TagSchemaItemTypeUtil';
import {getTagSchemaSummaryFromListingWithFields} from 'tag-schema/util/TagSchemaUtil';
import {capitalize, typeToName} from 'util/NameUtil';

import {TagSchemaListingWithFieldsGraphQL} from '../../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import {
  DataParameterFieldType,
  DataParameterType,
  DEFAULT_DATA_PARAMETER_TYPE,
  MaterialType,
  ProcedureSchemaFieldType,
  SUPPORTED_DATA_PARAMETER_FIELD_TYPES,
} from './procedureSchemaFieldConstants';

// Minimal shape needed to hydrate a fieldset link with its entity item type. Structurally satisfied by
// the fieldsets returned from useAllSchemaInterfacesQuery (which select baseBioentityType).
interface HydratableSchemaInterface {
  id: string;
  name: string;
  baseBioentityType?: {folderItemType: string; sequenceType: string | null} | null;
}

type ValidFolderItemCategory = 'basic_folder_item' | 'protein' | 'sequence' | 'molecule' | 'mixture';

const VALID_FOLDER_ITEM_CATEGORY_TYPES: Array<ValidFolderItemCategory> = [
  ModelTypeConstants.BASIC_FOLDER_ITEM,
  ModelTypeConstants.PROTEIN,
  ModelTypeConstants.SEQUENCE,
  ModelTypeConstants.MOLECULE,
  ModelTypeConstants.MIXTURE,
];

function hydrateRequiredLinkFromId(
  tagSchemaId: string,
  tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>,
  displayName: string
): RequiredLink | null {
  const schema = tagSchemas.find(({id}) => id === tagSchemaId);

  if (schema == null) {
    notifications.error(
      `Could not find schema '${displayName}'. This may be due to pasting values from another tenant.`
    );
    return null;
  }
  return {
    tagSchema: getTagSchemaSummaryFromListingWithFields(schema),
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: null,
  };
}

function hydrateRequiredLinkFromSchemaInterfaceId(
  schemaInterfaceId: string,
  // null means the fieldset list is still loading; we skip hydration silently rather than erroring.
  schemaInterfaces: Array<HydratableSchemaInterface> | null,
  displayName: string
): RequiredLink | null {
  if (schemaInterfaces == null) {
    return null;
  }
  const schemaInterface = schemaInterfaces.find(({id}) => id === schemaInterfaceId);

  if (schemaInterface == null) {
    notifications.error(
      `Could not find fieldset '${displayName}'. This may be due to pasting values from another tenant.`
    );
    return null;
  }
  const baseBioentityType = schemaInterface.baseBioentityType;
  return {
    tagSchema: null,
    entitySchemaFieldPath: null,
    folderItemType: null,
    schemaInterface: {
      id: schemaInterface.id,
      name: schemaInterface.name,
      itemType:
        baseBioentityType != null
          ? folderItemTypeAndSequenceTypeToItemType(
              baseBioentityType.folderItemType as TagSchemaSupportedFolderItemType,
              baseBioentityType.sequenceType as TagSchemaSequenceSchemaType | null
            )
          : null,
    } as CustomSchemaInterfaceSummary,
  };
}

export function convertTypedValueToMaterialType(
  {fieldDefinition, displayName}: ProcedureSchemaFieldTypeTypedValue,
  tagSchemas: Array<TagSchemaListingWithFieldsGraphQL>,
  // null means the fieldset list is still loading (skip hydration silently); [] means none exist.
  // Default to null so callers that don't pass a list skip fieldset hydration instead of erroring.
  schemaInterfaces: Array<HydratableSchemaInterface> | null = null
): MaterialType | null {
  // Specific entity schema
  if (fieldDefinition.linkedTagSchemaId != null) {
    const requiredLink = hydrateRequiredLinkFromId(
      fieldDefinition.linkedTagSchemaId,
      tagSchemas,
      displayName
    );
    return requiredLink != null
      ? {
          type: FieldType.BIOENTITY_LINK,
          requiredLink,
          unit: null,
        }
      : null; // If we weren't able to hydrate the required link, return null for the caller to handle
  } else if (fieldDefinition.linkedSchemaInterfaceId != null) {
    // Fieldset (custom schema interface)
    const requiredLink = hydrateRequiredLinkFromSchemaInterfaceId(
      fieldDefinition.linkedSchemaInterfaceId,
      schemaInterfaces,
      displayName
    );
    return requiredLink != null
      ? {
          type: FieldType.BIOENTITY_LINK,
          requiredLink,
          unit: null,
        }
      : null; // If we weren't able to hydrate the required link, return null for the caller to handle
  } else if (fieldDefinition.linkedFolderItemType == null) {
    if (fieldDefinition.fieldType === FieldType.TEXT) {
      // Text
      return {
        type: FieldType.TEXT,
        requiredLink: null,
        unit: null,
      };
    } else if (fieldDefinition.fieldType === FieldType.BIOENTITY_LINK) {
      // Any entity
      return {
        type: FieldType.BIOENTITY_LINK,
        requiredLink: {
          folderItemType: null,
          tagSchema: null,
          entitySchemaFieldPath: null,
          schemaInterface: null,
        },
        unit: null,
      };
    }
    assert(false, 'Unknown material', {fieldDefinition});
  } else if (VALID_FOLDER_ITEM_CATEGORY_TYPES.includes(fieldDefinition.linkedFolderItemType)) {
    // Protein / Sequence / Molecule
    return {
      type: FieldType.BIOENTITY_LINK,
      requiredLink: {
        folderItemType: fieldDefinition.linkedFolderItemType,
        tagSchema: null,
        entitySchemaFieldPath: null,
        schemaInterface: null,
      },
      unit: null,
    };
  }
  assert(false, 'Unknown material', {fieldDefinition});
}

export function convertMaterialTypeToTypedValue(
  materialType: MaterialType
): ProcedureSchemaFieldTypeTypedValue {
  const type: string = materialType.type;
  if (type === FieldType.TEXT) {
    // Text
    assert(materialType.requiredLink == null, 'Text material should not have a required link');
    return {
      type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
      displayName: 'Text',
      fieldDefinition: {
        fieldType: FieldType.TEXT,
        linkedFolderItemType: null,
        linkedTagSchemaId: null,
        linkedSchemaInterfaceId: null,
      },
    };
  }

  const requiredLink = materialType.requiredLink;
  assert(type === FieldType.BIOENTITY_LINK, 'Expected a bioentity link type', {
    materialType,
  });
  assert(requiredLink != null, 'Expected a required link');

  // Fieldset (custom schema interface)
  if (isRequiredLinkCustomSchemaInterface(requiredLink)) {
    return {
      type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
      displayName: requiredLink.schemaInterface.name,
      fieldDefinition: {
        fieldType: FieldType.BIOENTITY_LINK,
        linkedFolderItemType: null,
        linkedTagSchemaId: null,
        linkedSchemaInterfaceId: requiredLink.schemaInterface.id,
      },
    };
  }

  const folderItemType = (requiredLink as {folderItemType: ValidFolderItemCategory | null}).folderItemType;
  const linkedTagSchema = (requiredLink as {tagSchema: {id: string; name: string} | null}).tagSchema;

  if (folderItemType == null) {
    if (linkedTagSchema == null) {
      assert(
        // eslint-disable-next-line benchlint/no-eqeqeq-null
        folderItemType === null && linkedTagSchema === null,
        'Expected an empty requiredLink'
      );
      // Any entity
      return {
        type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
        displayName: ANY_ENTITY.name,
        fieldDefinition: {
          fieldType: FieldType.BIOENTITY_LINK,
          linkedFolderItemType: null,
          linkedTagSchemaId: null,
          linkedSchemaInterfaceId: null,
        },
      };
    }
    // A specific entity schema
    return {
      type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
      displayName: linkedTagSchema.name,
      fieldDefinition: {
        fieldType: FieldType.BIOENTITY_LINK,
        linkedFolderItemType: null,
        linkedTagSchemaId: linkedTagSchema.id,
        linkedSchemaInterfaceId: null,
      },
    };
  } else if (VALID_FOLDER_ITEM_CATEGORY_TYPES.includes(folderItemType)) {
    // Protein / Sequence / Molecule / etc.
    return {
      type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
      displayName: capitalize(typeToName(folderItemType)),
      fieldDefinition: {
        fieldType: FieldType.BIOENTITY_LINK,
        linkedFolderItemType: folderItemType,
        linkedTagSchemaId: null,
        linkedSchemaInterfaceId: null,
      },
    };
  }
  assert(false, 'Unknown material type', {materialType});
}

export function convertMaterialTypeToSchemaFieldDataType(
  materialType: MaterialType,
  isMulti: boolean
): SchemaFieldDataType {
  return {
    fieldType: materialType.type,
    requiredLink: materialType.requiredLink,
    isRequired: true,
    isMulti,
  };
}

export function convertTypedValueToDataParameterType({
  fieldDefinition,
}: ProcedureSchemaFieldTypeTypedValue): DataParameterType {
  const fieldType = fieldDefinition.fieldType as DataParameterFieldType;

  if (fieldDefinition.linkedTagSchemaId == null && SUPPORTED_DATA_PARAMETER_FIELD_TYPES.includes(fieldType)) {
    return {
      type: fieldType,
      requiredLink: null,
      unit: null,
    };
  }
  return DEFAULT_DATA_PARAMETER_TYPE;
}

const DATA_PARAMETER_TYPE_TO_DISPLAY_NAME: {[Key in FieldType]?: string} = {
  [FieldType.TEXT]: 'Text',
  [FieldType.FLOAT]: 'Decimal',
  [FieldType.DATETIME]: 'Datetime',
};

export function convertDataParameterTypeToTypedValue(
  dataParameterType: DataParameterType
): ProcedureSchemaFieldTypeTypedValue {
  const type = dataParameterType.type;
  return convertDataParameterFieldTypeToTypedValue(type);
}

export function convertDataParameterFieldTypeToTypedValue(
  fieldType: DataParameterFieldType
): ProcedureSchemaFieldTypeTypedValue {
  if (SUPPORTED_DATA_PARAMETER_FIELD_TYPES.includes(fieldType)) {
    return {
      type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
      displayName: DATA_PARAMETER_TYPE_TO_DISPLAY_NAME[fieldType] ?? fieldType,
      fieldDefinition: {
        fieldType,
        linkedFolderItemType: null,
        linkedTagSchemaId: null,
        linkedSchemaInterfaceId: null,
      },
    };
  }
  assert(false, 'Misconfigured data parameter type');
  return {
    type: BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE,
    displayName: 'Text',
    fieldDefinition: {
      fieldType: FieldType.TEXT,
      linkedFolderItemType: null,
      linkedTagSchemaId: null,
      linkedSchemaInterfaceId: null,
    },
  };
}

// The SchemaField representation is needed by FieldDefinitionInputOrDisplay to track the current selection
export function convertToSchemaField(schemaFieldType: ProcedureSchemaFieldType): SchemaField {
  return {
    apiId: '',
    dbId: 0,
    id: 0,
    archiveRecord: null,
    name: '',
    displayName: '',
    systemName: '',
    isRequired: false,
    isComputed: false,
    isConvertedFromLink: false,
    isSnapshot: false,
    isIntegration: false,
    isMulti: false,
    isParentLink: false,
    description: null,
    schemaFieldSelectorId: null,
    fieldType: schemaFieldType.type,
    requiredLink: schemaFieldType.requiredLink,
  };
}

export function doesTypedValueHaveLinkedSchema(typedValue: TypedValue | null | undefined): boolean {
  return (
    typedValue?.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE &&
    typedValue.fieldDefinition.fieldType === FieldType.BIOENTITY_LINK &&
    typedValue.fieldDefinition.linkedTagSchemaId != null
  );
}

export function doesTypedValueHaveLinkedSchemaInterface(typedValue: TypedValue | null | undefined): boolean {
  return (
    typedValue?.type === BaseTypedValueType.PROCEDURE_SCHEMA_FIELD_TYPE &&
    typedValue.fieldDefinition.fieldType === FieldType.BIOENTITY_LINK &&
    typedValue.fieldDefinition.linkedSchemaInterfaceId != null
  );
}
